import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { websAPI, banksAPI, rfcsAPI } from '../../services/api';

const Webs = () => {
  const [webs, setWebs] = useState([]);
  const [banks, setBanks] = useState([]);
  const [rfcs, setRfcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingWeb, setEditingWeb] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    quote_prefix: '',
    bank_id: '',
    rfc_id: '',
    active: true
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [websRes, banksRes, rfcsRes] = await Promise.all([
        websAPI.getAll(),
        banksAPI.getAll(),
        rfcsAPI.getAll()
      ]);
      setWebs(websRes.data.webs);
      setBanks(banksRes.data.banks);
      setRfcs(rfcsRes.data.rfcs);
    } catch (err) {
      setError('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (web = null) => {
    if (web) {
      setEditingWeb(web);
      setFormData({
        name: web.name,
        code: web.code || '',
        quote_prefix: web.quote_prefix || '',
        bank_id: web.bank_id || '',
        rfc_id: web.rfc_id || '',
        active: web.is_active
      });
    } else {
      setEditingWeb(null);
      setFormData({
        name: '',
        code: '',
        quote_prefix: '',
        bank_id: '',
        rfc_id: '',
        active: true
      });
    }
    setFormError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingWeb(null);
    setFormError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);

    try {
      const data = {
        ...formData,
        bank_id: formData.bank_id || null,
        rfc_id: formData.rfc_id || null
      };

      if (editingWeb) {
        await websAPI.update(editingWeb.id, data);
      } else {
        await websAPI.create(data);
      }
      closeModal();
      fetchData();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (web, force = false) => {
    const confirmMsg = force
      ? `¿ELIMINAR FORZADO la web "${web.name}"?\n\nEsto eliminará también todas las cotizaciones asociadas. Esta acción no se puede deshacer.`
      : `¿Eliminar la web "${web.name}"?`;

    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      await websAPI.delete(web.id, force);
      fetchData();
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Error al eliminar';
      if (errorMsg.includes('cotizaciones asociadas') && !force) {
        if (window.confirm(`${errorMsg}\n\n¿Desea eliminar forzadamente la web y todas sus cotizaciones?`)) {
          handleDelete(web, true);
        }
      } else {
        alert(errorMsg);
      }
    }
  };

  const toggleStatus = async (web) => {
    try {
      await websAPI.update(web.id, {
        ...web,
        active: !web.is_active
      });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.message || 'Error al actualizar estado');
    }
  };

  return (
    <AdminLayout>
      <>
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Webs / Sitios</h1>
            <p className="text-gray-600">Configura las webs y sus asociaciones de banco y RFC</p>
          </div>
          <button onClick={() => openModal()} className="btn btn-primary">
            + Nueva Web
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Web
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Código / Prefijo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Banco Asociado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    RFC Asociado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                      <p className="mt-2 text-gray-500">Cargando...</p>
                    </td>
                  </tr>
                ) : webs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      No hay webs registradas
                    </td>
                  </tr>
                ) : (
                  webs.map((web) => (
                    <tr key={web.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <p className="font-medium text-gray-900">{web.name}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-mono text-gray-900">{web.code}</p>
                          <p className="text-xs text-gray-500">Prefijo: {web.quote_prefix}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {web.bank_name ? (
                          <div>
                            <p className="text-sm text-gray-900">{web.holder_name}</p>
                            <p className="text-xs text-gray-500">{web.bank_name}</p>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400 italic">Sin asignar</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {web.rfc_code ? (
                          <div>
                            <p className="text-sm font-mono text-gray-900">{web.rfc_code}</p>
                            <p className="text-xs text-gray-500">{web.business_name}</p>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400 italic">Sin asignar</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => toggleStatus(web)}
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            web.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {web.is_active ? 'Activa' : 'Inactiva'}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-right space-x-2">
                        <button
                          onClick={() => openModal(web)}
                          className="text-primary-600 hover:text-primary-800 text-sm"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(web)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {editingWeb ? 'Editar Web' : 'Nueva Web'}
              </h2>

              {formError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  {formError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="label">Nombre de la Web *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="input"
                      placeholder="Ej: Mi Tienda Online"
                      required
                    />
                  </div>

                  <div>
                    <label className="label">Código *</label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      className="input"
                      placeholder="Ej: TIENDA01"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Identificador único de la web</p>
                  </div>

                  <div>
                    <label className="label">Prefijo para Cotizaciones *</label>
                    <input
                      type="text"
                      value={formData.quote_prefix}
                      onChange={(e) => setFormData({ ...formData, quote_prefix: e.target.value.toUpperCase() })}
                      className="input"
                      placeholder="Ej: ASJFGG"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Se usará en el número de cotización (ej: ASJFGG-4002)</p>
                  </div>

                  <div>
                    <label className="label">Banco Asociado</label>
                    <select
                      value={formData.bank_id}
                      onChange={(e) => setFormData({ ...formData, bank_id: e.target.value })}
                      className="input"
                    >
                      <option value="">-- Seleccionar banco --</option>
                      {banks.map((bank) => (
                        <option key={bank.id} value={bank.id}>
                          {bank.holder_name} ({bank.bank_name})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="label">RFC Asociado</label>
                    <select
                      value={formData.rfc_id}
                      onChange={(e) => setFormData({ ...formData, rfc_id: e.target.value })}
                      className="input"
                    >
                      <option value="">-- Seleccionar RFC --</option>
                      {rfcs.map((rfc) => (
                        <option key={rfc.id} value={rfc.id}>
                          {rfc.rfc_code} - {rfc.business_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.active}
                        onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 h-4 w-4"
                      />
                      <span className="ml-2 text-sm text-gray-700">Web activa (visible para vendedores)</span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button type="button" onClick={closeModal} className="btn btn-secondary">
                    Cancelar
                  </button>
                  <button type="submit" disabled={saving} className="btn btn-primary">
                    {saving ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Webs;
